/**
 * Configuration for a landing page
 */

// Define login method enum to match API expectations
export enum LoginMethod {
  SMS = "SMS",
  EMAIL = "EMAIL",
}

export enum AffiliateDestination {
  TEST_DESTINATION = "TestDestination",
  TEST_DESTINATION_2 = "TestDestination2",
}

export interface AgeCohortConfig {
  minAgeInclusive: number;
  maxAgeExclusive: number;
  trialId: string;
}

/**
 * Configuration for deprioritizing specific trial sites
 * Sites in this list will be used only as a fallback when no other trials have available sites
 */
export interface DeprioritizedTrialSites {
  /** Trial ID this configuration applies to */
  trialId: string;
  /** Array of trial site IDs to deprioritize */
  siteIds: string[];
}

/**
 * Configuration for conditional trial routing based on questionnaire answers
 * Used when trial selection depends on medical criteria (BMI, conditions, etc.)
 * and site availability needs to be checked before final selection
 */
export interface ConditionalTrialRouting {
  /**
   * Function that determines eligible trials based on questionnaire answers
   * Returns trials in priority order (will be tried sequentially until one has a site)
   */
  getEligibleTrials: (answers: Record<string, any>, age?: number) => string[];

  /**
   * Optional configuration for deprioritizing specific trial sites
   * When a site match is found but the site ID is in this list, the system will:
   * 1. Continue trying other trials in the priority order
   * 2. Fall back to the deprioritized site only if no other options exist
   *
   * Use case: Temporarily deprioritize sites that aren't yet activated
   */
  deprioritizedSites?: DeprioritizedTrialSites[];
}

export type TrialIdConfig =
  | { defaultTrialId: string; ageCohorts?: AgeCohortConfig[] }
  | { defaultTrialId?: string; ageCohorts: AgeCohortConfig[] }
  | { conditionalRouting: ConditionalTrialRouting };

export type LandingPageStatus =
  | "active"
  | "development"
  | "testing"
  | "inactive";

export interface LandingPageConfig {
  // Basic page metadata
  id: string;
  title: string;
  description: string;
  status: LandingPageStatus;

  // Branding
  partnerName?: string;
  partnerLogoUrl?: string;

  // Hero section
  heroTitle: string;
  heroSubtitle?: string;

  // Additional content
  introText?: string;
  introTextClassName?: string;
  longDescription?: string;

  hideProgressBarOnFirstQuestion?: boolean;
  hideProgressBarQuestionCount?: boolean;
  prescreeningTitle?: string;

  // Pre-screening questionnaire
  preScreeningQuestions?: PreScreeningQuestion[];

  // HealthMatch integration
  conditionId: number; // The condition area ID for HealthMatch API integration
  trialIdToAutoApply?: string | TrialIdConfig; // Trial ID to automatically apply to
  preferredLoginMethod?: LoginMethod; // Preferred login method

  // Country configuration
  countryCode: string;

  // Tracking
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;

  // Redirect URL (if different from default)
  redirectUrl?: string;

  // Internal path to redirect to when the page is inactive (e.g. "/cond/382").
  // Defaults to the home page. Query params (UTMs) are preserved.
  inactiveRedirectPath?: string;

  // External redirect URL template (supports variables like $patientId, $priorityStudyId, $landingPageId, $anonymousId)
  externalRedirectUrl?: string;

  // Custom button text for submit button
  submitButtonText?: string;

  // Affiliate destinations
  affiliateDestinations?: AffiliateDestination[];

  redirectConditionId?: number; // Condition ID to use when redirecting to classic HealthMatch questionnaire - defaults to conditionId

  // Alternative flow: skip contact form and redirect immediately after qualification
  skipContactFormOnQualified?: boolean; // When true, skip contact form and show immediate redirect screen
  qualifiedRedirectMessage?: string; // Custom message to show on qualified redirect screen

  // Collect EMR consent and patient's full address on the contact form
  requireEmrConsent?: boolean;

  // Thank you page configuration (when no redirect is desired)
  thankYouPage?: {
    bodyText: string; // Main message to display on the thank you page
  };

  // A/B testing redirect URL (can be provided via `vb` query param instead)
  abTestVariantBUrl?: string;

  // Direct redirect: immediately shows countdown and redirects to externalRedirectUrl.
  // Used for studies that handle their own pre-screening on an external site.
  directRedirect?: boolean;

  // Optional blood-pressure-history image upload prompt shown on the thank-you
  // page. When set, a deferrable upload UI is rendered below the existing
  // confirmation card, and a shareable /bp-upload/:token deep link is issued
  // for users who want to send their readings later.
  bpUpload?: BpUploadConfig;

  // Canonical campaign (cp=) for this study, used to reconstruct attribution when the
  // query param is stripped in transit. Campaign is study-specific, so it lives on the
  // landing (one landing serves all partners). See resolveAttribution() in utils/attribution.ts.
  defaultCampaign?: string;
}

export interface BpUploadConfig {
  headline: string;
  bodyText: string;
  instructionTitle: string;
  instructionText: string;
  ctaLabel: string;
  laterCtaLabel: string;
  successHeadline: string;
  successBody: string;
  /** Cap on images per submit (and total recommended). Defaults handled in component. */
  maxImages?: number;
}

/**
 * Template types for landing pages
 */
export enum LandingPageTemplate {
  EXTENDED = "extended",
  // Add more templates as needed
}

/**
 * Configuration for a landing page with template type
 */
export interface LandingPageWithTemplate extends LandingPageConfig {
  template: LandingPageTemplate;
}

/**
 * Configuration for a landing page variant
 * Only includes the fields that are different from the master configuration
 */
export interface LandingPageVariant
  extends Partial<Omit<LandingPageWithTemplate, "id">> {
  id: string; // Variant ID is required
  masterId: string; // Reference to the master landing page
  status: LandingPageStatus;
}

/**
 * Configuration for a landing page with variants
 */
export interface LandingPageWithVariants extends LandingPageWithTemplate {
  variants?: Record<string, LandingPageVariant>;
}

/**
 * Configuration for a single study within a multi-study landing page
 */
export interface StudyConfig {
  studyId: string; // Unique identifier for this study
  studyName: string; // Display name for the study
  trialIdToAutoApply: string | TrialIdConfig; // Trial ID to submit to
  priority: number; // Lower number = higher priority (checked first)
  eligibleZipCodes: readonly string[]; // Array of eligible zipcodes for this study
  additionalQuestions?: PreScreeningQuestion[]; // Study-specific questions (asked after common questions)
  commonAnswerRequirements?: Record<string, string[]>; // Required values for common questions (e.g., age_eligibility: ["65_or_older"])
  disqualifyMessage?: string; // Custom disqualification message for this study
}

/**
 * Configuration for a multi-study landing page
 * Allows matching patients to multiple studies based on location and eligibility
 */
export interface MultiStudyLandingPageConfig extends LandingPageWithTemplate {
  isMultiStudy: true; // Flag to indicate this is a multi-study page
  studies: StudyConfig[]; // Array of studies to check
  commonPreScreeningQuestions: PreScreeningQuestion[]; // Questions asked to all patients before zipcode
}

/**
 * Type guard to check if a landing page config is a multi-study config
 */
export function isMultiStudyConfig(
  config: LandingPageConfig | LandingPageWithTemplate | undefined,
): config is MultiStudyLandingPageConfig {
  return !!config && "isMultiStudy" in config && config.isMultiStudy === true;
}

/**
 * Types of pre-screening questions
 */
export enum QuestionType {
  RADIO = "RADIO",
  CHECKBOX = "CHECKBOX",
  SELECT = "SELECT",
  TEXT = "TEXT",
  NUMBER = "NUMBER",
  RADIO_GRID = "RADIO_GRID",
  AI_MEDICATION_ENTRY = "AI_MEDICATION_ENTRY",
}

/**
 * Option for multiple choice questions
 */
export interface QuestionOption {
  value: string;
  label: string;
  sublabel?: string;
  dynamicLabel?: (allAnswers: Record<string, string | string[]>) => string;
  disqualify?:
    | boolean
    | ((
        allAnswers: Record<
          string,
          string | string[] | number | null | undefined
        >,
        currentAnswer?: string | string[],
      ) => boolean);
  disqualifyMessage?: string;
  allowFutureContact?: boolean;
  skipNextQuestions?:
    | number
    | ((
        allAnswers: Record<
          string,
          string | string[] | number | null | undefined
        >,
      ) => number);
  redirectToLandingPageId?: string; // Landing page ID to redirect to when this option is selected and disqualifies
  exclusive?: boolean; // When true, selecting this option deselects all others (for checkbox questions)
}

/**
 * Grid layout configuration for RADIO_GRID type questions
 */
export interface RadioGridLayout {
  columnHeaders: string[];
  rows: {
    label: string;
    sublabel?: string;
    values: {
      value: string;
      disqualify?:
        | boolean
        | ((
            allAnswers: Record<
              string,
              string | string[] | number | null | undefined
            >,
            currentAnswer?: string | string[],
          ) => boolean);
    }[];
  }[];
}

export class RadioGridQuickSelect {
  constructor(
    public label: string,
    public setValue: string,
    public submitButtonText?: string,
    public submitButtonSubtext?: {
      allText: string;
      remainingText: string;
    },
    public defaultValueSuffix?: string,
  ) {}

  static createMedicationQuickSelect(): RadioGridQuickSelect {
    return new RadioGridQuickSelect(
      "I have never taken any of these medications",
      "never",
      "Submit my medications",
      {
        allText:
          "All medications in this group will be marked as 'never taken'",
        remainingText:
          "Remaining medications in this group will be marked as 'never taken'",
      },
      "_never",
    );
  }
}

/**
 * Pre-screening question configuration
 */
import {
  AttributeValueInput,
  QuestionnaireAnswers,
} from "../utils/attributeMapping/types";

// A standard mapper function signature
export type MapperFunction = (
  response: any,
  context: { countryCode: string; allAnswers: QuestionnaireAnswers },
) => AttributeValueInput[];

// AI Medication Entry configuration
export interface AIMedicationCategory {
  category: string;
  medications: string[];
  disqualifyIfCurrent?: boolean;
  disqualifyWithin?: string;
  timeframes?: Record<string, string>;
  excludes?: string[];
}

export interface AIConfig {
  studyContext: string;
  medicationCategories: AIMedicationCategory[];
  experimentName?: string;
  /** Minimum number of distinct medication categories required to pass (inclusion logic) */
  minimumCategories?: number;
  /** Custom disqualify message when minimumCategories threshold is not met */
  minimumCategoriesMessage?: string;
}

export interface PreScreeningQuestion {
  id: string;
  type: QuestionType;
  question: string;
  allAnswers?: Record<string, string | string[]>;
  required?: boolean;
  helpText?: string;
  placeholder?: string;
  defaultValue?: string; // Pre-filled value for the question
  validation?: {
    pattern: RegExp;
    message: string;
  };
  suffix?: string;
  options?: QuestionOption[];
  disqualifyMessage?: string;
  allowBack?: boolean;
  minBMI?: number | ((allAnswers: Record<string, string | string[]>) => number);
  maxBMI?: number;
  minWeight?: number; // Minimum weight in lbs
  maxWeight?: number; // Maximum weight in lbs
  gridLayout?: RadioGridLayout;
  quickSelect?: RadioGridQuickSelect;
  attributeMapping?: MapperFunction;
  aiConfig?: AIConfig; // Configuration for AI medication entry
}

// Standard height options from 4'2" (127cm) to 6'8" (203cm)
export const standardHeightOptions: QuestionOption[] = [
  { value: "127", label: "4'2\" (127 cm)" },
  { value: "130", label: "4'3\" (130 cm)" },
  { value: "132", label: "4'4\" (132 cm)" },
  { value: "135", label: "4'5\" (135 cm)" },
  { value: "137", label: "4'6\" (137 cm)" },
  { value: "140", label: "4'7\" (140 cm)" },
  { value: "142", label: "4'8\" (142 cm)" },
  { value: "145", label: "4'9\" (145 cm)" },
  { value: "147", label: "4'10\" (147 cm)" },
  { value: "150", label: "4'11\" (150 cm)" },
  { value: "152", label: "5'0\" (152 cm)" },
  { value: "155", label: "5'1\" (155 cm)" },
  { value: "157", label: "5'2\" (157 cm)" },
  { value: "160", label: "5'3\" (160 cm)" },
  { value: "163", label: "5'4\" (163 cm)" },
  { value: "165", label: "5'5\" (165 cm)" },
  { value: "168", label: "5'6\" (168 cm)" },
  { value: "170", label: "5'7\" (170 cm)" },
  { value: "173", label: "5'8\" (173 cm)" },
  { value: "175", label: "5'9\" (175 cm)" },
  { value: "178", label: "5'10\" (178 cm)" },
  { value: "180", label: "5'11\" (180 cm)" },
  { value: "183", label: "6'0\" (183 cm)" },
  { value: "185", label: "6'1\" (185 cm)" },
  { value: "188", label: "6'2\" (188 cm)" },
  { value: "191", label: "6'3\" (191 cm)" },
  { value: "193", label: "6'4\" (193 cm)" },
  { value: "196", label: "6'5\" (196 cm)" },
  { value: "198", label: "6'6\" (198 cm)" },
  { value: "201", label: "6'7\" (201 cm)" },
  { value: "203", label: "6'8\" (203 cm)" },
];

// Standard height question configuration
export const standardHeightQuestion: PreScreeningQuestion = {
  id: "height",
  type: QuestionType.SELECT,
  question: "What is your height?",
  required: true,
  options: standardHeightOptions,
};

// Standard weight question configuration
export const standardWeightQuestion: PreScreeningQuestion = {
  id: "weight",
  type: QuestionType.NUMBER,
  question: "What is your weight in pounds (lbs)?",
  required: true,
  helpText:
    "We'll calculate your BMI (Body Mass Index) using your height and weight.",
  placeholder: "Enter weight",
  suffix: "lbs",
};
